import { useEffect, useState } from "react";
import { api } from "./api";
import { avatarColor } from "./cells";
import type { Item, Update, User } from "./types";

function initials(s: string) {
  return s.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}
function when(ts: number) {
  return new Date(ts).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// Right-hand drawer showing an item's "Updates" conversation (Monday-style).
export function ItemDrawer({ item, user, onClose, onCountChange }: {
  item: Item; user: User; onClose: () => void; onCountChange: (itemId: string, n: number) => void;
}) {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.updates(item.id);
      setUpdates(r.updates);
      onCountChange(item.id, r.count);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [item.id]);

  async function post() {
    if (!body.trim()) return;
    await api.postUpdate(item.id, body.trim());
    setBody("");
    await load();
  }
  async function reply(parentId: string) {
    if (!replyBody.trim()) return;
    await api.postUpdate(item.id, replyBody.trim(), parentId);
    setReplyBody(""); setReplyTo(null);
    await load();
  }
  async function remove(id: string) {
    await api.deleteUpdate(id);
    await load();
  }

  const Avatar = ({ name }: { name: string }) => (
    <span className="avatar" style={{ background: avatarColor(name), width: 30, height: 30 }}>{initials(name)}</span>
  );

  return (
    <div className="overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <strong style={{ fontSize: 16 }}>{item.name}</strong>
          <button className="group-caret" onClick={onClose} style={{ fontSize: 18 }}>×</button>
        </div>
        <div className="drawer-tab">Updates {updates.length ? `/ ${updates.length}` : ""}</div>

        <div className="update-composer">
          <textarea className="input" rows={2} placeholder="Schreibe eine Aktualisierung…"
            value={body} onChange={(e) => setBody(e.target.value)} />
          <div style={{ textAlign: "right", marginTop: 6 }}>
            <button className="btn btn-sm" disabled={!body.trim()} onClick={post}>Aktualisierung posten</button>
          </div>
        </div>

        <div className="updates-list">
          {loading ? <div className="spin" /> : !updates.length ? (
            <p className="muted" style={{ textAlign: "center", marginTop: 30 }}>Noch keine Updates. Starte die Konversation.</p>
          ) : updates.map((u) => (
            <div className="update-card" key={u.id}>
              <div className="row-flex" style={{ justifyContent: "space-between" }}>
                <div className="row-flex"><Avatar name={u.user_name || u.user_email} />
                  <div><strong>{u.user_name || u.user_email}</strong><div className="muted" style={{ fontSize: 11 }}>{when(u.created_at)}</div></div>
                </div>
                {(u.user_id === user.id || user.isAdmin) && (
                  <span style={{ cursor: "pointer", color: "var(--text-faint)" }} onClick={() => remove(u.id)}>🗑</span>
                )}
              </div>
              <div className="update-body">{u.body}</div>

              {u.replies.map((r) => (
                <div className="update-reply" key={r.id}>
                  <div className="row-flex"><Avatar name={r.user_name || r.user_email} />
                    <div><strong>{r.user_name || r.user_email}</strong><div className="muted" style={{ fontSize: 11 }}>{when(r.created_at)}</div></div>
                  </div>
                  <div className="update-body">{r.body}</div>
                </div>
              ))}

              {replyTo === u.id ? (
                <div style={{ marginTop: 8 }}>
                  <input className="input" autoFocus placeholder="Antworten…" value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && reply(u.id)} />
                  <div style={{ textAlign: "right", marginTop: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setReplyTo(null); setReplyBody(""); }}>Abbrechen</button>{" "}
                    <button className="btn btn-sm" onClick={() => reply(u.id)}>Senden</button>
                  </div>
                </div>
              ) : (
                <button className="reply-btn" onClick={() => { setReplyTo(u.id); setReplyBody(""); }}>↩ Antworten</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
